import React from "react";
import { MoreVertical, Edit, Trash2, Eye } from "lucide-react";
import { GameTable } from "../../api/services/gameTables/type";

interface Props {
  gameTables: GameTable[];
  onEdit?: (table: GameTable) => void;
  onDelete?: (id: string) => void;
  onView?: (table: GameTable) => void;
  isLoading?: boolean;
}

export const GameTableTable: React.FC<Props> = ({
  gameTables,
  onEdit,
  onDelete,
  onView,
  isLoading = false,
}) => {
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);

  const handleMenuToggle = (id: string) =>
    setOpenMenuId(openMenuId === id ? null : id);
  const handleAction = (action: () => void) => {
    action();
    setOpenMenuId(null);
  };

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-slate-400">Loading game tables...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg overflow-visible">
      <table className="w-full">
        <thead className="bg-slate-700">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Table Name
            </th>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Min Amount
            </th>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Max Amount
            </th>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Is Limit
            </th>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Limit Number
            </th>
            <th className="px-6 py-4 text-center text-xs font-medium text-slate-300 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700">
          {gameTables.map((table) => (
            <tr key={table.id} className="hover:bg-slate-700 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap text-white">
                {table.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-white">
                {table.minAmount}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-white">
                {table.maxAmount}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-white">
                {table.isLimit ? "Yes" : "No"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-white">
                {table.limitNumber}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                <div className="relative inline-block">
                  <button
                    onClick={() => handleMenuToggle(table.id)}
                    className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-600 transition-colors"
                  >
                    <MoreVertical size={16} />
                  </button>
                  {openMenuId === table.id && (
                    <div className="absolute right-0 mt-2 w-48 bg-slate-700 rounded-md shadow-lg z-10 border border-slate-600">
                      <div className="py-1">
                        {onView && (
                          <button
                            onClick={() => handleAction(() => onView(table))}
                            className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 hover:text-white transition-colors"
                          >
                            <Eye size={16} className="mr-3" />
                            View Details
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={() => handleAction(() => onEdit(table))}
                            className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 hover:text-white transition-colors"
                          >
                            <Edit size={16} className="mr-3" />
                            Edit Table
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={() =>
                              handleAction(() => onDelete(table.id))
                            }
                            className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-red-600 hover:text-white transition-colors"
                          >
                            <Trash2 size={16} className="mr-3" />
                            Delete Table
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {gameTables.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-slate-400">No game tables found</div>
        </div>
      )}
      {openMenuId && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setOpenMenuId(null)}
        />
      )}
    </div>
  );
};
